// apps/web/src/self-service/SelfServiceCancelledPage.tsx — Phase 4 plan 04-04.
//
// A11 — the post-cancel confirmation. Renders after a successful
// `Mutation.cancelReservationByToken`; the cancel page navigates
// here via `/reservierung/:token/storniert`. The page clears the
// in-memory token + reservation on the "Neue Reservierung" link
// click so the next booking flow is forced to navigate via a
// fresh createReservation (T-04-32).

import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { clearSelfService } from "./state";
import { resetWizard } from "../wizard/state";
import { isValidSelfServiceToken } from "./token-shape";
import { NotFoundCard } from "./NotFoundCard";

export function SelfServiceCancelledPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ token: string }>();

  // Phase 4 follow-up #2: the /reservierung/:token/storniert route
  // shares the same :token catch-all as the read/modify/cancel
  // surface, so the same shape-mismatch guard applies here. Without
  // it, /reservierung/datum/storniert would render the
  // "Reservierung wurde storniert" page for a URL that never had a
  // matching reservation. Note: the SelfServiceShell dispatch
  // (main.tsx:180) already protects A8/A9/A10 — this file stands
  // alone on /storniert, so the check has to live here too.
  if (!isValidSelfServiceToken(params.token)) {
    return (
      <main
        className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
        data-testid="a11-cancelled-page"
      >
        <NotFoundCard />
      </main>
    );
  }

  function newReservation() {
    // Drop both stores so the next wizard run starts from a clean
    // slate (the modify / cancel stores still hold the cancelled
    // row's metadata; the wizard store still holds the previous
    // booking's slot + guest).
    clearSelfService();
    resetWizard();
    navigate("/reservieren/datum");
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a11-cancelled-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="px-4 py-3 border-b border-divider"
          data-testid="a11-header"
        >
          <h1
            className="font-heading text-2xl md:text-3xl"
            data-testid="a11-heading"
          >
            {t("wizard.a11.title")}
          </h1>
        </header>
        <div className="px-4 pt-4 pb-6">
          <p className="text-sm text-text/80" data-testid="a11-body">
            {t("wizard.a11.body")}
          </p>
          <button
            type="button"
            onClick={newReservation}
            data-testid="a11-new-action"
            className="mt-4 min-h-[44px] px-4 py-2 rounded-md border border-accent text-accent-700 hover:bg-accent-100 inline-flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            <span>{t("wizard.a11.newAction")}</span>
          </button>
        </div>
      </div>
    </main>
  );
}
